/**
 * State auctions: land the state and municipalities sell or lease
 * (emlak.gov.az weekly notices) and the state e-auction's property lots
 * (auksion.gov.az). Types, API calls and the formatters every surface
 * shares (the hub, the lot page, the land map overlay, the alerts).
 */
import { API_BASE } from "@/lib/api";
import type { TranslationKey } from "@/lib/i18n";

export type AuctionKind = "land" | "residential" | "premises";
export type AuctionDeal = "sale" | "lease";
export type AuctionStatus = "open" | "closed" | "held" | "sold" | "unsold" | "withdrawn";
export type GeoPrecision = "street" | "settlement" | "district" | "none";

export interface AuctionCard {
  id: string;
  source: "emlak_land" | "auksion";
  kind: AuctionKind;
  deal: AuctionDeal;
  procedure: "auction" | "tender";
  title: string | null;
  region: string | null;
  region_slug: string | null;
  settlement: string | null;
  municipality: string | null;
  area_m2: number | null;
  start_price: number | null;
  price_per_sot: number | null;
  price_per_m2: number | null;
  lease_years: number | null;
  purpose: string | null;
  status: AuctionStatus;
  application_deadline: string | null;
  auction_at: string | null;
  attempt: number;
  first_price: number | null;
  discount_pct: number | null;
  lat: number | null;
  lng: number | null;
  geo_precision: GeoPrecision;
  first_seen_at: string | null;
}

export interface AuctionDetail extends AuctionCard {
  address: string | null;
  land_category: string | null;
  land_use: string | null;
  bonitet: number | null;
  registry_no: string | null;
  bid_step: number | null;
  deposit_pct: number | null;
  normative_price: number | null;
  conditions: string | null;
  viewing: string | null;
  office: string | null;
  office_phone: string | null;
  announced_at: string | null;
  notice_url: string | null;
  notice_title: string | null;
  category_name: string | null;
}

export interface AuctionMarket {
  radius_km: number;
  n: number;
  median: number;
  unit: "sot" | "m2";
  start_vs_market_pct: number | null;
}

export interface AuctionLotResponse {
  lot: AuctionDetail;
  history: { id: string; auction_at: string | null; start_price: number | null; status: AuctionStatus }[];
  market: AuctionMarket | null;
  nearby: AuctionCard[];
}

export interface AuctionRegion {
  slug: string;
  name: string;
  active: number;
  year: number;
  sale: number;
  lease: number;
  reoffered: number;
  median_sale_per_sot: number | null;
  center: [number, number] | null;
  kinds: Partial<Record<AuctionKind, number>>;
}

export interface AuctionFilters {
  status?: "active" | "open" | "past" | "all";
  kind?: AuctionKind[];
  deal?: AuctionDeal | "";
  region?: string[];
  min_area?: number | null;
  max_area?: number | null;
  max_price?: number | null;
  discounted?: boolean;
  sort?: "deadline" | "newest" | "price" | "price_desc" | "area" | "discount";
  /** Free text over the lot's words (title, purpose, place). */
  q?: string;
  limit?: number;
  offset?: number;
}

export interface AuctionAlert {
  id: number;
  name: string;
  filters: { regions: string[]; kinds: AuctionKind[]; deal: AuctionDeal | null; max_price: number | null; min_area_m2: number | null; max_area_m2: number | null };
  is_active: boolean;
  created_at: string | null;
  last_notified_at: string | null;
}

export function filtersToQuery(f: AuctionFilters): URLSearchParams {
  const q = new URLSearchParams();
  if (f.status) q.set("status", f.status);
  (f.kind ?? []).forEach((k) => q.append("kind", k));
  if (f.deal) q.set("deal", f.deal);
  (f.region ?? []).forEach((r) => q.append("region", r));
  if (f.min_area) q.set("min_area", String(f.min_area));
  if (f.max_area) q.set("max_area", String(f.max_area));
  if (f.max_price) q.set("max_price", String(f.max_price));
  if (f.discounted) q.set("discounted", "true");
  if (f.sort) q.set("sort", f.sort);
  if (f.q) q.set("q", f.q);
  if (f.limit) q.set("limit", String(f.limit));
  if (f.offset) q.set("offset", String(f.offset));
  return q;
}

async function getJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include", ...init });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error((body as { detail?: string }).detail || `HTTP ${res.status}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export const auctionsApi = {
  list: (f: AuctionFilters) => getJSON<{ total: number; items: AuctionCard[] }>(`/state-auctions?${filtersToQuery(f)}`),
  geo: (f: AuctionFilters) => getJSON<GeoJSON.FeatureCollection<GeoJSON.Point, Partial<AuctionCard>>>(`/state-auctions/geo?${filtersToQuery(f)}`),
  regions: () => getJSON<{ regions: AuctionRegion[] }>(`/state-auctions/regions`),
  lot: (id: string) => getJSON<AuctionLotResponse>(`/state-auctions/lot/${encodeURIComponent(id)}`),
  alerts: () => getJSON<{ alerts: AuctionAlert[] }>(`/state-auctions/alerts`),
  createAlert: (body: { name: string; filters: AuctionAlert["filters"]; locale?: string }) =>
    getJSON<AuctionAlert>(`/state-auctions/alerts`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  patchAlert: (id: number, body: Partial<Pick<AuctionAlert, "is_active" | "name" | "filters">>) =>
    getJSON<AuctionAlert>(`/state-auctions/alerts/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  deleteAlert: (id: number) => getJSON<void>(`/state-auctions/alerts/${id}`, { method: "DELETE" }),
};

/** One line for an auction alert's filters ("Sabirabad · Torpaq · ≤ 50 000 AZN"),
 *  shared by the hub's alert panel and the /alerts page. */
export function auctionAlertSummary(
  f: AuctionAlert["filters"],
  t: (key: TranslationKey) => string,
  regionName: (slug: string) => string,
): string {
  const bits: string[] = [];
  // Named regions only: "all districts" said nothing (owner, 2026-10-09).
  if (f.regions?.length) bits.push(f.regions.map(regionName).join(", "));
  if (f.kinds?.length) bits.push(f.kinds.map((k) => t(`sa.kind.${k}` as TranslationKey)).join(", "));
  if (f.deal) bits.push(t(`sa.deal.${f.deal}` as TranslationKey));
  if (f.max_price) bits.push(`≤ ${money(f.max_price)} AZN`);
  if (f.min_area_m2 || f.max_area_m2) bits.push(`${f.min_area_m2 ? Math.round(f.min_area_m2 / 100) : 0}-${f.max_area_m2 ? Math.round(f.max_area_m2 / 100) : "∞"} sot`);
  return bits.join(" · ");
}

/** The hub URL that shows the lots an auction alert watches. */
export function auctionAlertHref(f: AuctionAlert["filters"]): string {
  const q = new URLSearchParams();
  (f.kinds ?? []).forEach((k) => q.append("kind", k));
  if (f.deal) q.set("deal", f.deal);
  (f.regions ?? []).forEach((r) => q.append("region", r));
  if (f.max_price) q.set("max_price", String(f.max_price));
  if (f.min_area_m2) q.set("min_sot", String(Math.round(f.min_area_m2 / 100)));
  if (f.max_area_m2) q.set("max_sot", String(Math.round(f.max_area_m2 / 100)));
  const qs = q.toString();
  return qs ? `/auctions?${qs}` : "/auctions";
}

/** Thousands with a no-break space, as everywhere else in the app. */
export function money(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** Land in sot under a hectare, hectares above; buildings in m². */
export function areaLabel(m2: number | null | undefined, kind: AuctionKind, locale?: string): string | null {
  if (!m2) return null;
  const [ha, sot, sqm] = locale === "ru" ? ["га", "сот", "м²"] : ["ha", "sot", "m²"];
  if (kind !== "land") return `${money(m2)}\u00A0${sqm}`;
  if (m2 >= 10000) return `${(m2 / 10000).toFixed(2).replace(/\.?0+$/, "")}\u00A0${ha}`;
  return `${(m2 / 100).toFixed(1).replace(/\.0$/, "")}\u00A0${sot}`;
}

const BAKU_MS = 4 * 3600 * 1000;
const MONTHS: Record<string, string[]> = {
  az: ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"],
  ru: ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

/** "2 noyabr, 18:00" in Baku time, whatever the viewer's clock says. */
export function bakuDate(iso: string | null | undefined, locale: string, withTime = false): string | null {
  if (!iso) return null;
  const d = new Date(new Date(iso).getTime() + BAKU_MS);
  const m = (MONTHS[locale] ?? MONTHS.az)[d.getUTCMonth()];
  const day = locale === "en" ? `${m} ${d.getUTCDate()}` : `${d.getUTCDate()} ${m}`;
  const year = d.getUTCFullYear() !== new Date().getUTCFullYear() ? ` ${d.getUTCFullYear()}` : "";
  if (!withTime) return day + year;
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${day}${year}, ${hh}:${mm}`;
}

/** Whole days left until an instant (0 = today), or null once past. */
export function daysLeft(iso: string | null | undefined, now: number = Date.now()): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - now;
  if (ms < 0) return null;
  return Math.floor(ms / 86400000);
}

/** "3-cü" / "3rd" / "3-й": the ordinal the attempt line needs. Azerbaijani
 *  suffixes follow the vowel of the number's last spoken word. */
export function ordinal(n: number, locale: string): string {
  if (locale === "ru") return `${n}-й`;
  if (locale === "en") {
    const r100 = n % 100;
    const s = r100 >= 11 && r100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
    return `${n}${s}`;
  }
  const last = n % 10;
  const tens = n % 100;
  let suf: string;
  if (last !== 0) suf = ({ 1: "ci", 2: "ci", 3: "cü", 4: "cü", 5: "ci", 6: "cı", 7: "ci", 8: "ci", 9: "cu" } as Record<number, string>)[last];
  else if (tens !== 0) suf = ({ 10: "cu", 20: "ci", 30: "cu", 40: "cı", 50: "ci", 60: "cı", 70: "ci", 80: "ci", 90: "cı" } as Record<number, string>)[tens];
  else suf = "cü";
  return `${n}-${suf}`;
}

export function lotHref(id: string): string {
  return `/auctions/${id}`;
}

/** The colour a status reads in (semantic scale only). */
export function statusTone(s: AuctionStatus): "emerald" | "amber" | "gray" | "rose" {
  if (s === "open") return "emerald";
  if (s === "closed") return "amber";
  if (s === "withdrawn") return "rose";
  return "gray";
}


/** Lots of one village share the village-centre coordinate. Fan such a
 *  stack into a small ring (about 150 m, wider for big stacks) so every
 *  lot gets its own pin; the place is still "the village", which the
 *  lot page says. */
export function spreadStacked<F extends GeoJSON.Feature<GeoJSON.Point>>(fc: { type: "FeatureCollection"; features: F[] }): { type: "FeatureCollection"; features: F[] } {
  const groups = new Map<string, F[]>();
  for (const f of fc.features) {
    const [x, y] = f.geometry.coordinates;
    const k = `${x.toFixed(4)},${y.toFixed(4)}`;
    const g = groups.get(k);
    if (g) g.push(f); else groups.set(k, [f]);
  }
  const out: F[] = [];
  for (const g of groups.values()) {
    if (g.length === 1) { out.push(g[0]); continue; }
    const r = 0.0014 * Math.max(1, Math.sqrt(g.length / 6));
    g.forEach((f, i) => {
      const [x, y] = f.geometry.coordinates;
      const a = (2 * Math.PI * i) / g.length;
      out.push({ ...f, geometry: { ...f.geometry, coordinates: [x + (r * Math.cos(a)) / Math.cos((y * Math.PI) / 180), y + r * Math.sin(a)] } });
    });
  }
  return { type: "FeatureCollection", features: out };
}
